import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Paper,
  Grid,
  TextField,
  InputAdornment,
  Alert,
  Chip,
  Stack,
  IconButton,
  useTheme,
  alpha,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import {
  Close as CloseIcon,
  AccountBalanceWallet as WalletIcon,
} from '@mui/icons-material';

const AdvancePaymentSelectorDialog = ({
  open,
  onClose,
  customerId,
  customerName,
  advanceBalance,
  billTotal,
  onUseAdvance,
  onSkip
}) => {
  const theme = useTheme();
  // const isDark = theme.palette.mode === 'dark';
  const [useAdvance, setUseAdvance] = useState(false);
  const [usageAmounts, setUsageAmounts] = useState({
    cash: '',
    online: '',
    cheque: '',
    oldGold: '',
    oldSilver: '',
    other: ''
  });

  const availableBalances = advanceBalance?.byMode || {
    cash: 0, online: 0, cheque: 0, oldGold: 0, oldSilver: 0, other: 0
  };
  const totalAvailable = advanceBalance?.total || 0;

  useEffect(() => {
    // Reset when dialog opens
    if (open) {
      setUseAdvance(false);
      setUsageAmounts({
        cash: '', online: '', cheque: '', oldGold: '', oldSilver: '', other: ''
      });
    }
  }, [open]);

  const calculateTotalUsing = () => {
    return Object.values(usageAmounts).reduce((sum, val) => sum + (parseFloat(val) || 0), 0);
  };

  const handleUsageChange = (mode, value) => {
    const amount = parseFloat(value) || 0;
    const maxAvailable = availableBalances[mode] || 0;

    if (amount > maxAvailable) {
      alert(`Cannot use more than available advance of ₹${maxAvailable.toFixed(2)} for ${mode}`);
      return;
    }

    setUsageAmounts(prev => ({ ...prev, [mode]: value }));
  };

  // Inside AdvancePaymentSelectorDialog component
  const handleUseAdvance = () => {
    if (!useAdvance) {
      onSkip();
      onClose();
      return;
    }

    const totalToUse = calculateTotalUsing();
    if (totalToUse <= 0) {
      alert('Please enter amount to use from advance');
      return;
    }

    if (totalToUse > billTotal) {
      alert(`Cannot use advance more than bill total of ₹${billTotal.toFixed(2)}`);
      return;
    }

    // Filter out modes with zero usage
    const filteredUsageAmounts = {};
    Object.entries(usageAmounts).forEach(([mode, amount]) => {
      const numAmount = parseFloat(amount);
      if (numAmount > 0) {
        filteredUsageAmounts[mode] = numAmount;
      }
    });

    // Call the parent callback (this is the handleUseAdvance from CustomerTransaction)
    onUseAdvance({
      usageAmounts: filteredUsageAmounts,
      totalAmount: totalToUse
    });

    onClose();
  };

  const totalToUse = calculateTotalUsing();

  if (!advanceBalance || totalAvailable === 0) {
    return null;
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <WalletIcon color="primary" />
            <Typography variant="h6" fontWeight={700}>
              Available Advance Payment
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
        <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
          Customer: {customerName}
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={3}>
          {/* Available Balance Summary */}
          <Paper sx={{ p: 2, bgcolor: alpha(theme.palette.success.main, 0.1), borderRadius: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="subtitle1" fontWeight={600}>
                Available Advance Balance
              </Typography>
              <Chip
                label={`Total: ₹${totalAvailable.toFixed(2)}`}
                color="success"
                sx={{ fontWeight: 600 }}
              />
            </Box>

            <Grid container spacing={1} sx={{ mt: 1 }}>
              {availableBalances.cash > 0 && (
                <Grid item xs={4}>
                  <Chip
                    label={`Cash: ₹${availableBalances.cash.toFixed(2)}`}
                    size="small"
                    variant="outlined"
                  />
                </Grid>
              )}
              {availableBalances.online > 0 && (
                <Grid item xs={4}>
                  <Chip
                    label={`Online: ₹${availableBalances.online.toFixed(2)}`}
                    size="small"
                    variant="outlined"
                  />
                </Grid>
              )}
              {availableBalances.cheque > 0 && (
                <Grid item xs={4}>
                  <Chip
                    label={`Cheque: ₹${availableBalances.cheque.toFixed(2)}`}
                    size="small"
                    variant="outlined"
                  />
                </Grid>
              )}
              {availableBalances.oldGold > 0 && (
                <Grid item xs={4}>
                  <Chip
                    label={`Old Gold: ₹${availableBalances.oldGold.toFixed(2)}`}
                    size="small"
                    variant="outlined"
                  />
                </Grid>
              )}
              {availableBalances.oldSilver > 0 && (
                <Grid item xs={4}>
                  <Chip
                    label={`Old Silver: ₹${availableBalances.oldSilver.toFixed(2)}`}
                    size="small"
                    variant="outlined"
                  />
                </Grid>
              )}
              {availableBalances.other > 0 && (
                <Grid item xs={4}>
                  <Chip
                    label={`Other: ₹${availableBalances.other.toFixed(2)}`}
                    size="small"
                    variant="outlined"
                  />
                </Grid>
              )}
            </Grid>
          </Paper>

          {/* Option to use advance */}
          <FormControlLabel
            control={
              <Checkbox
                checked={useAdvance}
                onChange={(e) => setUseAdvance(e.target.checked)}
                color="primary"
              />
            }
            label={
              <Typography variant="subtitle1" fontWeight={600}>
                Use Advance Payment for this Bill
              </Typography>
            }
          />

          {useAdvance && (
            <Box sx={{ pl: 2 }}>
              <Alert severity="info" sx={{ mb: 2 }}>
                You can use partial amounts from each payment mode.
                The used amounts will be deducted from available advance.
              </Alert>

              <Grid container spacing={2}>
                {availableBalances.cash > 0 && (
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label={`Cash (Available: ₹${availableBalances.cash.toFixed(2)})`}
                      type="number"
                      value={usageAmounts.cash}
                      onChange={(e) => handleUsageChange('cash', e.target.value)}
                      fullWidth
                      size="small"
                      InputProps={{
                        startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                      }}
                    />
                  </Grid>
                )}

                {availableBalances.online > 0 && (
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label={`Online (Available: ₹${availableBalances.online.toFixed(2)})`}
                      type="number"
                      value={usageAmounts.online}
                      onChange={(e) => handleUsageChange('online', e.target.value)}
                      fullWidth
                      size="small"
                      InputProps={{
                        startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                      }}
                    />
                  </Grid>
                )}

                {availableBalances.cheque > 0 && (
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label={`Cheque (Available: ₹${availableBalances.cheque.toFixed(2)})`}
                      type="number"
                      value={usageAmounts.cheque}
                      onChange={(e) => handleUsageChange('cheque', e.target.value)}
                      fullWidth
                      size="small"
                      InputProps={{
                        startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                      }}
                    />
                  </Grid>
                )}

                {availableBalances.oldGold > 0 && (
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label={`Old Gold (Available: ₹${availableBalances.oldGold.toFixed(2)})`}
                      type="number"
                      value={usageAmounts.oldGold}
                      onChange={(e) => handleUsageChange('oldGold', e.target.value)}
                      fullWidth
                      size="small"
                      InputProps={{
                        startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                      }}
                    />
                  </Grid>
                )}

                {availableBalances.oldSilver > 0 && (
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label={`Old Silver (Available: ₹${availableBalances.oldSilver.toFixed(2)})`}
                      type="number"
                      value={usageAmounts.oldSilver}
                      onChange={(e) => handleUsageChange('oldSilver', e.target.value)}
                      fullWidth
                      size="small"
                      InputProps={{
                        startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                      }}
                    />
                  </Grid>
                )}
              </Grid>

              {totalToUse > 0 && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2">
                      Total advance to use: <strong>₹{totalToUse.toFixed(2)}</strong>
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Remaining after bill: <strong>₹{(totalAvailable - totalToUse).toFixed(2)}</strong>
                    </Typography>
                  </Box>
                </Alert>
              )}
            </Box>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} variant="outlined">
          Cancel
        </Button>
        <Button
          onClick={handleUseAdvance}
          variant="contained"
          disabled={useAdvance && totalToUse <= 0}
          sx={{
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
            }
          }}
        >
          {useAdvance ? `Use Advance (₹${totalToUse.toFixed(2)})` : 'Skip Advance'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AdvancePaymentSelectorDialog;